import bravida from '@/assets/bravida_logo_rgb_pos.png';
import RssFeedRoundedIcon from '@mui/icons-material/RssFeedRounded';
import { Box, LinearProgress, Typography } from '@mui/material';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import {
  deriveLatestRunMeta,
  LatestRunOrder,
} from '../../utils/deriveLatestRun';
import SubscriptionActionsCell from './SubscriptionActionsCell';
import SubscriptionDataGridToolbar from './SubscriptionDataGridToolbar';
import SubscriptionLatestRunCell from './SubscriptionLatestRunCell';
import SubscriptionStateCell from './SubscriptionStateCell';
import SubscriptionStatusCell from './SubscriptionStatusCell';

import { daDK } from '@mui/x-data-grid/locales';

import DataGridNoRowsOverlay from '@/components/datagrid/DataGridNoRowsOverlay';
import { useLocalStorageJson } from '@/hooks/useLocalStorageJson';
import { getQualityOfService } from '@/features/building-optimization-operations-dashboard/utils/utilityFunctions';
import { useMemo } from 'react';
import HtmlTooltip from '@/components/tooltip/HtmlTooltip';
import { GppGoodRounded } from '@mui/icons-material';

export default function SubscriptionDataGrid({
  subscriptions,
  accounts,
  onSubscriptionCreate,
  pauseSubscription,
  resumeSubscription,
  activateSubscription,
  resetInitialization,
  handleViewSubscription,
  handleEditSubscription,
  handleDeleteSubscription,
  handleAlgorithmInputOpen,
  handleAlgorithmInputFormOpen,
  handleOccupancyPredictionAnalysisOpen,
}) {
  const enrichedSubscriptions = useMemo(() => {
    if (!subscriptions || !accounts) return [];

    const accountMap = new Map(
      accounts.map((account) => [account.AccountId, account]),
    );

    return subscriptions.map((sub) => ({
      ...sub,
      Account: accountMap.get(sub.AccountId) || null,
    }));
  }, [subscriptions, accounts]);

  const rows = enrichedSubscriptions.map((row) => {
    const { status, warnings, errors } = deriveLatestRunMeta(row);

    return {
      ...row,
      JobSubscriptionId: row.JobSubscriptionId,
      AccountName: row?.Account?.Name ?? null,
      LatestComfortMinTemp:
        row?.State?.ComfortHealthCheck?.Data?.LatestMinComfortTemperature ??
        null,
      TargetComfortTemp:
        row?.State?.TargetTemperatureStatus?.TargetTemperature ?? null,
      StateCode: row.StateCode ?? null,
      HeatingSystemType: row?.UserInput?.HeatingSystemType ?? null,
      GatewayUptime:
        row?.State?.GatewayHealthCheck?.Data?.UptimePercentage ?? 0,
      LatestRunStatus: status, // 👈 sortable/groupable field
      LatestRunWarnings: warnings, // for tooltip
      LatestRunErrors: errors, // for tooltip
      QoS: getQualityOfService(row),
      BuildingProtection: row?.State?.BuildingProtection ?? null,
      Status: row?.State ?? null,
    };
  });

  console.log('enrichedSubscriptions', enrichedSubscriptions);

  const [dataGridState, setDataGridState] = useLocalStorageJson(
    `Subscription-DataGrid-Presets`,
    {
      presets: [
        {
          name: 'Default',
          visibilityList: {},
          density: 'standard',
          sortModel: [],
          filterModel: { items: [], quickFilterValues: [] },
        },
      ],
      chosenPreset: 0,
    },
  );

  const updateChosenPreset = (patch) =>
    setDataGridState((prev) => ({
      ...prev,
      presets: prev.presets.map((preset, idx) =>
        idx === prev.chosenPreset ? { ...preset, ...patch } : preset,
      ),
    }));

  const handleVisibilityChange = (model) => {
    updateChosenPreset({ visibilityList: model });
  };

  const handleDensityChange = (newDensity) => {
    updateChosenPreset({ density: newDensity });
  };

  const handleSortModelChange = (newModel) => {
    updateChosenPreset({ sortModel: newModel });
  };

  const handleFilterModelChange = (newModel) => {
    updateChosenPreset({ filterModel: newModel });
  };

  const currentPreset = dataGridState.presets[dataGridState.chosenPreset];

  const columns = [
    {
      field: 'JobSubscriptionId',
      headerName: 'JobSubscription ID',
      flex: 1,
      hide: true,
    },
    {
      field: 'AccountId',
      headerName: 'Account ID',
      flex: 1,
      hide: true,
    },
    {
      field: 'AccountId',
      headerName: 'Account ID',
      flex: 1,
      hide: true,
    },
    {
      field: 'AccountName',
      headerName: 'Account Name',
      flex: 1,
    },
    {
      field: 'LatestRunStatus',
      headerName: 'Latest Run',
      flex: 1,
      sortable: true,
      groupable: true,
      type: 'singleSelect',
      valueOptions: LatestRunOrder,
      sortComparator: (a, b) =>
        LatestRunOrder.indexOf(a) - LatestRunOrder.indexOf(b),
      renderCell: (params) => (
        <SubscriptionLatestRunCell
          status={params.value}
          warnings={params.row.LatestRunWarnings}
          errors={params.row.LatestRunErrors}
        />
      ),
    },
    {
      field: 'ControlType',
      headerName: 'Control Type',
      sortable: true,
      flex: 1,
      renderCell: (params) => {
        const controlType = params?.row?.UserInput?.ControlType;

        if (controlType === 'ecl') {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <span>{controlType.toUpperCase()}</span>
              {/* <RssFeedRoundedIcon /> */}
            </Box>
          );
        } else if (controlType === 'bravida') {
          return (
            <Box sx={{ display: 'flex', width: '100%', height: '100%' }}>
              <img
                src={bravida}
                alt="Bravida Logo"
                style={{ height: '24px', marginTop: '8px' }}
              />
            </Box>
          );
        } else if (controlType === 'pct') {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <span>{controlType.toUpperCase()}</span>
              {/* <div
                style={{
                  width: '25px',
                  height: '25px',
                  backgroundColor: '#323232',
                  borderRadius: '40%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  marginRight: '0.5em',
                }}
              >
                <div
                  style={{
                    width: '12px',
                    height: '12px',
                    border: '1.5px solid #fff',
                    borderRadius: '50%',
                  }}
                ></div>
              </div> */}
            </Box>
          );
        } else {
          return '';
        }
      },
    },
    { field: 'DisplayName', headerName: 'Name', flex: 1 },
    {
      field: 'StateCode',
      headerName: 'State',
      flex: 1,
      sortable: true,
      renderCell: (params) => (
        <SubscriptionStateCell
          params={params}
          pauseSubscription={pauseSubscription}
          resumeSubscription={resumeSubscription}
          activateSubscription={activateSubscription}
          resetInitialization={resetInitialization}
        />
      ),
    },
    {
      field: 'HeatingSystemType',
      headerName: 'Heating Type',
      flex: 1,
    },
    {
      field: 'LatestComfortMinTemp',
      headerName: 'Latest Comfort Temp',
      flex: 1,
      sortable: true,
      type: 'number',
      renderCell: (params) =>
        params.value != null ? `${params.value.toFixed(1)} °C` : '-',
    },
    {
      field: 'TargetComfortTemp',
      headerName: 'Target Comfort Temp',
      flex: 1,
      type: 'number',
      renderCell: (params) =>
        params.value != null ? `${params.value.toFixed(1)} °C` : '-',
    },
    {
      field: 'GatewayUptime',
      headerName: 'GW Uptime',
      flex: 1,
      renderCell: (params) => {
        const controlType = params?.row?.UserInput?.ControlType;
        if (controlType === 'ecl' || controlType === 'bravida') {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
              <Typography fontSize="0.8rem" color="text.secondary">
                N/A
              </Typography>
            </Box>
          );
        }

        const uptime =
          params?.row?.State?.GatewayHealthCheck?.Data?.UptimePercentage ?? 0;
        const getProgressColor = (value) => {
          if (value >= 90) return '#52b202';
          if (value >= 40) return (theme) => theme.palette.warning.light;
          return (theme) => theme.palette.error.light;
        };

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <LinearProgress
              variant="determinate"
              value={uptime}
              sx={{
                minWidth: 75,
                height: 10,
                borderRadius: 5,
                '& .MuiLinearProgress-bar': {
                  backgroundColor: getProgressColor(uptime),
                },
              }}
            />
            <Typography fontSize="0.8rem" color="text.secondary" sx={{ ml: 1 }}>
              {uptime.toFixed(0)}%
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      filterable: false,
      sortable: false,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" height="100%">
          <SubscriptionStatusCell state={params.row.State} />
        </Box>
      ),
    },
    {
      field: 'BuildingProtection',
      headerName: 'Building Protection',
      flex: 1,
      renderCell: (params) => {
        const now = new Date();
        const value = params.value;

        if (value) {
          const { AppliedBuildingProtectionPatches = [] } = value;

          const isRecentPatch = AppliedBuildingProtectionPatches.some(
            (patch) => {
              const intervalEnd = new Date(patch.interval_end);
              const timeDifference = now - intervalEnd; // difference in milliseconds
              return timeDifference <= 48 * 60 * 60 * 1000; // within 48 hours
            },
          );

          if (isRecentPatch) {
            return (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center', // Center vertically
                  justifyContent: 'center', // Center horizontally
                  width: '100%', // Take full width of the cell
                  height: '100%', // Take full height of the cell
                }}
              >
                <HtmlTooltip
                  title="Building Protection Patches has been applied in last 48 hours"
                  content={AppliedBuildingProtectionPatches?.map((interval) => {
                    return (
                      <div key={interval.interval_end}>
                        <strong>Interval Start:</strong>{' '}
                        {interval.interval_start}
                        <br></br>
                        <strong>Interval End:</strong> {interval.interval_end}
                        {value?.TriggeredSensors?.map((unit) => {
                          return (
                            <div key={unit.unit_id}>
                              <strong>Unit ID:</strong> {unit.unit_id}
                              <br></br>
                              <strong>Unit Type Input ID:</strong>{' '}
                              {unit.unit_type_input_id}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                >
                  <GppGoodRounded />
                </HtmlTooltip>
              </Box>
            );
          }
        }

        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center', // Center vertically
              justifyContent: 'center', // Center horizontally
              width: '100%', // Take full width of the cell
              height: '100%', // Take full height of the cell
            }}
          >
            -
          </Box>
        );
      },
    },
    {
      field: 'QoS',
      headerName: 'QoS',
      flex: 1,
      sortable: true,
      type: 'number',
      renderCell: (params) =>
        params.value != null ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
            }}
          >
            <Typography fontWeight="bold">{params.value.toFixed(1)}</Typography>
          </Box>
        ) : (
          '-'
        ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      filterable: false,
      width: 140,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <SubscriptionActionsCell
            row={params.row}
            onView={(row) => handleViewSubscription(row)}
            onEdit={(row) => handleEditSubscription(row)}
            onDelete={(row) => handleDeleteSubscription(row)}
            onAlgorithmInputOpen={(row) => handleAlgorithmInputOpen(row)}
            onAlgorithmInputFormOpen={(row) =>
              handleAlgorithmInputFormOpen(row)
            }
            onOccupancyPredictionAnalysisOpen={(row) =>
              handleOccupancyPredictionAnalysisOpen(row)
            }
          />
        </Box>
      ),
    },
  ].filter(Boolean); // 👈 filters out false/null (like if showControlType is false)

  return (
    <>
      <DataGridPremium
        key={dataGridState.chosenPreset}
        localeText={daDK.components.MuiDataGrid.defaultProps.localeText}
        rows={rows}
        columns={columns}
        columnHeaderHeight={36}
        getRowId={(r) => r.JobSubscriptionId}
        disableRowSelectionOnClick
        columnVisibilityModel={currentPreset.visibilityList}
        onColumnVisibilityModelChange={handleVisibilityChange}
        onDensityChange={(density) => handleDensityChange(density)}
        density={currentPreset.density}
        sortModel={currentPreset.sortModel}
        onSortModelChange={handleSortModelChange}
        filterModel={currentPreset.filterModel}
        onFilterModelChange={handleFilterModelChange}
        slots={{
          toolbar: SubscriptionDataGridToolbar,
          noRowsOverlay: DataGridNoRowsOverlay,
        }}
        slotProps={{
          toolbar: {
            onSubscriptionCreate,
            dataGridState: dataGridState,
            setDataGridState: setDataGridState,
            filterModel: currentPreset.filterModel,
          },
        }}
        sx={{
          borderRadius: 2,
          border: 'none',
          maxHeight: '85vh',
          '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within':
            {
              outline: 'none',
            },
          '& .hidden-column-cell, & .hidden-column-header': {
            display: 'none',
          },
          '& .MuiDatagrid-root': {
            border: 0,
          },
          '.MuiDataGrid-withBorderColor': {
            border: 'none !important',
          },
          '& .MuiDatagrid-footerContainer': {
            border: 0,
          },
          '& .MuiDatagrid-bottomContainer': {
            border: 0,
          },
          '& .MuiDataGrid-main': {
            backgroundColor: (theme) =>
              theme.palette.mode === 'light' ? '#fff' : '#121212',
            borderRadius: 2,
            border: (theme) => `1px solid ${theme.palette.divider}`,
          },
          '& .MuiDataGrid-columnHeaders': {
            borderBottom: '1px solid', // Optional: Add a border
            borderColor: 'divider',
          },
          '& .MuiDataGrid-columnsContainer': {
            backgroundColor: 'blue',
          },
          '& .selected-row': {
            backgroundColor: (theme) => theme.palette.action.selected,
            '&:hover': {
              backgroundColor: (theme) => theme.palette.action.selected,
            },
          },
          '& .MuiDataGrid-scrollbar': {
            overflow: 'hidden !important',
          },
        }}
      />
    </>
  );
}
